import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getReviews, getSettings } from '../lib/api';
import { Star } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ReviewsPage() {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getReviews().then(r => setReviews(r.data)).catch(console.error);
    getSettings().then(r => setSettings(r.data)).catch(console.error);
  }, []);

  const avgRating = settings.google_rating || 4.7;
  const reviewCount = settings.google_review_count || 186;

  return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)]">
      <section className="bg-[hsl(20,17%,12%)] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl sm:text-4xl lg:text-5xl tracking-[var(--tracking-display)] mb-3">{t('reviews_page_title')}</h1>
          <p className="text-white/70 max-w-lg mx-auto">{t('reviews_page_subtitle')}</p>
        </div>
      </section>

      <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-8 py-5 border border-[hsl(32,17%,84%)] shadow-sm">
            <span className="font-[family-name:var(--font-display)] text-4xl text-[hsl(16,55%,36%)]">{avgRating}</span>
            <div className="text-left">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`w-5 h-5 ${s <= Math.round(avgRating) ? 'fill-[hsl(42,72%,44%)] text-[hsl(42,72%,44%)]' : 'text-[hsl(32,17%,84%)]'}`} />
                ))}
              </div>
              <p className="text-xs text-[hsl(22,10%,38%)] mt-1">{t('based_on')} {reviewCount} {t('reviews_count')}</p>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, i) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white rounded-2xl p-6 border border-[hsl(32,17%,84%)] hover:shadow-[0_18px_44px_rgba(33,24,16,.10)] transition-shadow duration-300"
            >
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[hsl(42,72%,44%)] text-[hsl(42,72%,44%)]' : 'text-[hsl(32,17%,84%)]'}`} />
                ))}
              </div>
              <p className="text-sm text-[hsl(20,17%,12%)] mb-4 leading-relaxed">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{review.author}</span>
                <span className="text-xs text-[hsl(22,10%,38%)]">{review.date}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
